import type { ComponentType } from 'react';
import { X, TrendingUp, Coins, ShieldAlert, Link2, Clock, MessageSquare, Target, TriangleAlert as AlertTriangle, Award, Users, Package } from 'lucide-react';
import type { UseCaseItem } from '../../../types';
import BdrBattlecardSection from './BdrBattlecardSection';
import { sanitizeUrl, sanitizeTextUrls } from '../../../lib/sanitizeUrl';

interface Props {
  useCase: UseCaseItem;
  onClose: () => void;
}

function renderInline(text: string): (string | JSX.Element)[] {
  const parts: (string | JSX.Element)[] = [];
  const regex = /(\*\*(.+?)\*\*)|(\[([^\]]+)\]\(([^)]+)\))|(`([^`]+)`)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let keyIdx = 0;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    if (match[1]) {
      parts.push(<strong key={keyIdx++} className="font-semibold text-app-ink">{match[2]}</strong>);
    } else if (match[3]) {
      const href = sanitizeUrl(match[5]);
      parts.push(
        <a key={keyIdx++} href={href} target="_blank" rel="noopener noreferrer"
          className="text-forest-600 hover:text-forest-700 underline underline-offset-2">{match[4]}</a>
      );
    } else if (match[6]) {
      parts.push(<code key={keyIdx++} className="px-1 py-0.5 bg-app-canvas rounded text-[0.85em] font-mono">{match[7]}</code>);
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}

interface BulletItem {
  type: 'subtitle' | 'bullet';
  content: string;
}

function proseToBullets(raw: string): BulletItem[] {
  const cleaned = sanitizeTextUrls(raw);
  const items: BulletItem[] = [];
  const lines = cleaned.split('\n');

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    const stripped = trimmed
      .replace(/^#{2,}\s+/, '')
      .replace(/^[-*•]\s+/, '')
      .replace(/^\d+[.)]\s+/, '');

    if (/:\s*$/.test(stripped) && stripped.length < 80) {
      items.push({ type: 'subtitle', content: stripped.replace(/:\s*$/, '') });
    } else {
      const sentences = stripped.split(/(?<=\.)\s+(?=[A-Z])/);
      for (const s of sentences) {
        const t = s.trim();
        if (t) items.push({ type: 'bullet', content: t });
      }
    }
  }
  return items;
}

function ScoreBadge({ label, value, icon: Icon }: { label: string; value: number; icon: ComponentType<{ className?: string }> }) {
  const bg = value >= 7 ? 'bg-app-positive/10 border-app-positive' : value >= 4 ? 'bg-app-warning/10 border-app-warning' : 'bg-app-critical/10 border-app-critical';
  const text = value >= 7 ? 'text-app-positive' : value >= 4 ? 'text-app-warning' : 'text-app-critical';
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${bg}`}>
      <Icon className={`w-4 h-4 ${text}`} />
      <span className="text-xs font-medium text-app-muted">{label}</span>
      <span className={`text-sm font-bold ${text} ml-auto`}>{value}/10</span>
    </div>
  );
}

type Accent = 'forest' | 'blue' | 'amber' | 'red' | 'teal' | 'gray';

const accentMap: Record<Accent, { icon: string; heading: string; bar: string; subtitleText: string; bulletDot: string }> = {
  forest: { icon: 'text-forest-600', heading: 'text-forest-800', bar: 'bg-forest-500', subtitleText: 'text-forest-700', bulletDot: 'bg-forest-400' },
  blue:   { icon: 'text-app-focus',   heading: 'text-app-focus',   bar: 'bg-app-focus',   subtitleText: 'text-app-focus',   bulletDot: 'bg-app-focus' },
  amber:  { icon: 'text-app-warning',  heading: 'text-app-warning',  bar: 'bg-app-warning',  subtitleText: 'text-app-warning',  bulletDot: 'bg-app-warning' },
  red:    { icon: 'text-app-critical',    heading: 'text-app-critical',    bar: 'bg-app-critical',    subtitleText: 'text-app-critical',    bulletDot: 'bg-app-critical' },
  teal:   { icon: 'text-app-focus',   heading: 'text-app-focus',   bar: 'bg-app-focus/100',   subtitleText: 'text-app-focus',   bulletDot: 'bg-app-focus' },
  gray:   { icon: 'text-app-muted',   heading: 'text-app-ink',   bar: 'bg-app-muted',   subtitleText: 'text-app-ink',   bulletDot: 'bg-app-muted' },
};

function SectionBlock({ title, icon: Icon, text, accent = 'forest' }: {
  title: string;
  icon: ComponentType<{ className?: string }>;
  text: string;
  accent?: Accent;
}) {
  const c = accentMap[accent];
  const items = proseToBullets(text);

  return (
    <div className="relative">
      <div className={`absolute top-0 left-0 w-1 h-full rounded-full ${c.bar}`} />
      <div className="pl-5">
        <div className="flex items-center gap-2 mb-3">
          <Icon className={`w-5 h-5 ${c.icon}`} />
          <h3 className={`text-sm font-bold uppercase tracking-wider ${c.heading}`}>{title}</h3>
        </div>
        <div className="space-y-1.5">
          {items.map((item, i) =>
            item.type === 'subtitle' ? (
              <p key={i} className={`text-sm font-bold ${c.subtitleText} mt-3 first:mt-0`}>
                {renderInline(item.content)}
              </p>
            ) : (
              <div key={i} className="flex items-start gap-2">
                <div className={`w-1.5 h-1.5 rounded-full ${c.bulletDot} mt-[7px] flex-shrink-0`} />
                <p className="text-sm text-app-ink leading-relaxed">{renderInline(item.content)}</p>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}

function getProductNames(uc: UseCaseItem): string[] {
  if (uc.vendor_product_names && uc.vendor_product_names.length > 0) return uc.vendor_product_names;
  if (uc.vendor_product_name) return [uc.vendor_product_name];
  return [];
}

export default function UseCaseDetail({ useCase, onClose }: Props) {
  const scores = useCase.scores;
  const guidance = useCase.sales_conversation_guidance;
  const productNames = getProductNames(useCase);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 backdrop-blur-sm p-4 sm:p-8">
      <div className="bg-app-surface rounded-lg shadow-2xl w-full max-w-4xl my-4 relative">
        <div className="sticky top-0 z-10 bg-app-surface border-b border-app-line rounded-t-2xl px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-8 h-8 rounded-lg bg-forest-600 text-white flex items-center justify-center text-sm font-bold">
              #{useCase.rank}
            </span>
            <div>
              <h2 className="text-lg font-bold text-app-ink">{useCase.title}</h2>
              {productNames.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {productNames.map(name => (
                    <span key={name} className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-app-focus/10 text-app-focus text-xs font-semibold rounded-full border border-app-focus/30">
                      <Package className="w-3.5 h-3.5" />
                      {name}
                    </span>
                  ))}
                </div>
              )}
              <p className="text-sm text-app-muted mt-0.5">{useCase.sales_hook}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-app-muted hover:text-app-muted hover:bg-app-canvas rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-8">
          {useCase.bdr_battlecard && (
            <BdrBattlecardSection battlecard={useCase.bdr_battlecard} />
          )}

          <div>
            <h3 className="text-xs font-bold text-app-muted uppercase tracking-wider mb-3">Scoring Breakdown</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
              <ScoreBadge label="Revenue" value={scores.revenue} icon={TrendingUp} />
              <ScoreBadge label="Cost" value={scores.cost} icon={Coins} />
              <ScoreBadge label="Risk" value={scores.risk} icon={ShieldAlert} />
              <ScoreBadge label="Red Thread" value={scores.red_thread} icon={Link2} />
              <ScoreBadge label="TTV" value={scores.time_to_value} icon={Clock} />
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-forest-300 bg-forest-50">
                <Award className="w-4 h-4 text-forest-600" />
                <span className="text-xs font-medium text-app-muted">Total</span>
                <span className="text-sm font-bold text-forest-700 ml-auto">{scores.weighted_total.toFixed(1)}</span>
              </div>
            </div>
          </div>

          {useCase.red_thread_mapping && (
            <div className="p-4 bg-app-canvas rounded-lg border border-app-line">
              <p className="text-xs font-bold text-app-muted uppercase tracking-wider mb-1.5">Red Thread Mapping</p>
              <p className="text-sm text-app-ink leading-relaxed">{useCase.red_thread_mapping}</p>
            </div>
          )}

          {useCase.business_context && (
            <SectionBlock title="A) Use Case Business Context" icon={Target} accent="forest" text={useCase.business_context} />
          )}

          {useCase.strategic_alignment && (
            <SectionBlock title="B) Strategic Alignment (Red Thread)" icon={Link2} accent="blue" text={useCase.strategic_alignment} />
          )}

          {useCase.business_impact && (
            <SectionBlock title="C) Business Impact: Revenue, Cost, Risk" icon={TrendingUp} accent="teal" text={useCase.business_impact} />
          )}

          {useCase.why_traditional_fail && (
            <SectionBlock title="D) Why Traditional Solutions Fail" icon={AlertTriangle} accent="amber" text={useCase.why_traditional_fail} />
          )}

          {useCase.vendor_value_proposition && (
            <SectionBlock title="E) Vendor's Winning Value Proposition" icon={Award} accent="forest" text={useCase.vendor_value_proposition} />
          )}

          {guidance && (guidance.opening_hook || guidance.discovery_questions?.length > 0) && (
            <div className="bg-app-canvas rounded-lg border border-app-line p-6 space-y-5">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-app-muted" />
                <h3 className="text-sm font-bold text-app-ink uppercase tracking-wider">F) Sales Conversation Guidance</h3>
              </div>

              {guidance.opening_hook && (
                <div>
                  <p className="text-xs font-bold text-app-muted uppercase tracking-wider mb-1.5">Opening Hook</p>
                  <p className="text-sm text-app-ink leading-relaxed italic">"{guidance.opening_hook}"</p>
                </div>
              )}

              {guidance.discovery_questions && guidance.discovery_questions.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-app-muted uppercase tracking-wider mb-2">Discovery Questions</p>
                  <div className="space-y-2">
                    {guidance.discovery_questions.map((q, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-app-focus/10 text-app-focus flex items-center justify-center text-xs font-bold">{i + 1}</span>
                        <p className="text-sm text-app-ink leading-relaxed">{q}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {guidance.key_talking_points && guidance.key_talking_points.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-app-muted uppercase tracking-wider mb-2">Key Talking Points</p>
                  <div className="space-y-1.5">
                    {guidance.key_talking_points.map((point, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-forest-500 mt-1.5 flex-shrink-0" />
                        <p className="text-sm text-app-ink leading-relaxed">{point}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {guidance.objection_handling && guidance.objection_handling.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-app-muted uppercase tracking-wider mb-2">Objection Handling</p>
                  <div className="space-y-3">
                    {guidance.objection_handling.map((obj, i) => (
                      <div key={i} className="bg-app-surface rounded-lg border border-app-line p-4">
                        <div className="flex items-start gap-2 mb-2">
                          <Users className="w-4 h-4 text-app-critical flex-shrink-0 mt-0.5" />
                          <p className="text-sm font-medium text-app-critical">"{obj.objection}"</p>
                        </div>
                        <p className="text-sm text-app-ink leading-relaxed pl-6">{obj.response}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {guidance.success_metrics && guidance.success_metrics.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-app-muted uppercase tracking-wider mb-2">Success Metrics</p>
                  <div className="flex flex-wrap gap-2">
                    {guidance.success_metrics.map((metric, i) => (
                      <span key={i} className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium bg-forest-50 text-forest-700 border border-forest-200">
                        {metric}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {guidance.call_to_action && (
                <div className="p-3 bg-forest-50 rounded-lg border border-forest-200">
                  <p className="text-xs font-bold text-forest-600 uppercase tracking-wider mb-1">Call to Action</p>
                  <p className="text-sm text-forest-800 font-medium">{guidance.call_to_action}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
